import { create } from 'zustand';

/**
 * Per-surface override for the MobileShell top bar. A mobile page declares its
 * own title/subtitle/back affordance via {@link useMobileTopBar}; the shell
 * reads it and falls back to the route→title map when a page sets nothing.
 *
 * The config is stamped with the pathname that set it so the shell can ignore
 * a stale override the instant the route changes — a page that sets no config
 * never inherits the previous page's header.
 */
export interface MobileTopBarConfig {
  /** Replaces the route-derived title. */
  title?: string;
  /** Small line under the title (entity context, counts, status). */
  subtitle?: string;
  /** Show a back chevron that runs `onBack` (defaults to history back). */
  back?: boolean;
  /** Custom back handler; when omitted the shell navigates `-1`. */
  onBack?: () => void;
  /** Hide the bell + avatar cluster (e.g. immersive chat thread). */
  hideActions?: boolean;
  /** Drop the whole top bar — the surface owns its own header (chat thread). */
  hideHeader?: boolean;
  /** Remove the scroll container padding so the surface runs edge-to-edge. */
  fullBleed?: boolean;
}

interface MobileTopBarState {
  pathname: string | null;
  config: MobileTopBarConfig;
  set: (pathname: string, config: MobileTopBarConfig) => void;
  clear: (pathname: string) => void;
}

export const useMobileTopBarStore = create<MobileTopBarState>((set) => ({
  pathname: null,
  config: {},
  set: (pathname, config) => set({ pathname, config }),
  // Only clear when the caller still owns the slot — guards against a new
  // page's mount effect being wiped by the previous page's unmount cleanup.
  clear: (pathname) => set((s) => (s.pathname === pathname ? { pathname: null, config: {} } : s)),
}));
